////////LIBRARY/////////
import { useReducer, useEffect } from 'react'

function previous(lengthShowed: number, current: number, offset: number) {
	// To prevent modulo to set position at zero
	if (current === offset) {
		return current - 1
	} else {
		return (current - 1) % lengthShowed
	}
}

function next(lengthShowed: number, current: number) {
	// We can't use modulo so instead we use this patch
	return current < lengthShowed ? current + 1 : 0
}

const transitionTime = 400
const smooth = `transform ${transitionTime}ms ease`

interface CarouselState {
	offset_authorized: number
	desired: number
	active: number
}

const initialCarouselState: CarouselState = {
	offset_authorized: 0,
	desired: 0,
	active: 0,
}

interface CarouselNextAction {
	type: 'next'
	lengthShowed: number
}

interface CarouselPrevAction {
	type: 'prev'
	lengthShowed: number
}

interface CarouselJumpAction {
	type: 'jump'
	desired: number
}

interface CarouselDoneAction {
	type: 'done'
}

interface CarouselEndAction {
	type: 'end'
}

type CarouselAction = CarouselJumpAction | CarouselNextAction | CarouselPrevAction | CarouselDoneAction | CarouselEndAction

function carouselReducer(state: CarouselState, action: CarouselAction): CarouselState {
	switch (action.type) {
		case 'jump':
			return {
				...state,
				desired: action.desired,
			}
		case 'next':
			return {
				...state,
				desired: next(action.lengthShowed, state.active),
			}
		case 'prev':
			return {
				...state,
				desired: previous(action.lengthShowed, state.active, state.offset_authorized),
			}
		case 'done':
			return {
				...state,
				active: state.desired,
			}
		case 'end':
			return {
				...state,
				active: state.offset_authorized,
			}
		default:
			return state
	}
}

const style: React.CSSProperties = {
	transform: 'translateX(0)',
}

const styleOverflow: React.CSSProperties = {}

export function useCarousel(
	lengthShowed: number,
	length: number,
	carouselItemWidth: number
): [number, (n: number) => void, React.CSSProperties, React.CSSProperties] {
	const [state, dispatch] = useReducer(carouselReducer, initialCarouselState)
	const offset_authorized = length - lengthShowed > 0 ? length - lengthShowed : 0
	state.offset_authorized = offset_authorized

	// At loading of page, we need to check the diff between visibles slides and total slides,
	// so we can know how many times we can move the slides
	if (state === initialCarouselState) {
		// Loading of page
		state.desired = Math.round(offset_authorized / 2)
	}

	useEffect(() => {
		// If we come at end of sliders, we fix the state
		if (state.desired === state.offset_authorized) {
			const id = setTimeout(() => {
				dispatch({ type: 'end' })
			}, transitionTime)
			return () => clearTimeout(id)
		}
		// If superior, it is a next move
		if (state.desired > state.active) {
			const id = setTimeout(() => {
				dispatch({ type: 'next', lengthShowed })
				dispatch({ type: 'done' })
			}, transitionTime)
			return () => clearTimeout(id)
		}
		// Else a prev move
		if (state.desired < state.active) {
			const id = setTimeout(() => {
				dispatch({ type: 'prev', lengthShowed })
				dispatch({ type: 'done' })
			}, transitionTime)
			return () => clearTimeout(id)
		}
	}, [state.desired])

	if (state.desired !== state.active) {
		const shift = (100 * state.desired) / length
		style.transition = smooth

		style.transform = `translateX(-${shift}%)`
	}

	// Setup width div which contains all items
	style.width = `${length * carouselItemWidth + (length - 1) * 10}px`

	// Setup width div which overflow
	styleOverflow.width = `${lengthShowed * carouselItemWidth + (lengthShowed - 1) * 10}px`

	return [state.active, (n) => dispatch({ type: 'jump', desired: n }), style, styleOverflow]
}
